import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, merge } from 'rxjs';
import { debounceTime, finalize, take } from 'rxjs/operators';
import {
  ContactFormDialogData,
  ContactType,
  CreatePhonebookContactDTO,
  UpdatePhonebookContactDTO,
  PhonebookContactVM,
  IntroducerOption,
  PhonebookContactNoteVM,
} from '../../models';
import { PhonebookService } from '../../services/phonebook.service';
import { SearchableOption } from '../searchable-select/searchable-select.component';
import { CorporateFormsService } from '../../../services/corporate-forms.service';
import { IBusiness } from '../../../models/IBusiness.interface';
import { UserService } from '../../../../services/user.service';
import { CompaniesService } from '../../../../services/companies.service';
import { formAutosave } from 'src/app/shared/form-drafts/form-autosave';

interface TeamMemberOption {
  azureObjectId: string;
  name: string;
  email: string;
}

type FormSection = 'personal' | 'work' | 'lead' | 'location' | 'notes';

@Component({
    selector: 'app-contact-form-dialog',
    templateUrl: './contact-form-dialog.component.html',
    styleUrls: ['./contact-form-dialog.component.scss'],
    standalone: false
})
export class ContactFormDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  activeSection: FormSection = 'personal';
  isEdit = false;

  contactTypes: ContactType[] = [
    'Work', 'Client', 'Employee', 'Vendor', 'Consultant',
    'Legal', 'Bank', 'Government', 'Family Member', 'General',
  ];
  // A–Z in the picker, with the catch-all 'General' kept last.
  contactTypeSelectOptions: SearchableOption<string>[] = this.contactTypes
    .map((t) => ({ value: t, label: t as string }))
    .sort((a, b) => (a.value === 'General' ? 1 : b.value === 'General' ? -1 : a.label.localeCompare(b.label)));

  sections: { key: FormSection; label: string; icon: string }[] = [
    { key: 'personal', label: 'Personal', icon: 'person' },
    { key: 'work', label: 'Work', icon: 'business_center' },
    { key: 'lead', label: 'Lead', icon: 'trending_up' },
    { key: 'notes', label: 'Notes', icon: 'description' },
    { key: 'location', label: 'Location', icon: 'location_on' },
  ];

  // Industry options — fetched from the system's compliance industries.
  // Stable arrays (NOT getters) so the dropdown option DOM isn't recreated
  // every change-detection cycle (which would eat the click).
  industrySelectOptions: SearchableOption<string>[] = [];
  industriesLoading = false;
  teamMemberSelectOptions: SearchableOption<string>[] = [];
  introducerSelectOptions: SearchableOption<number>[] = [];

  // Preferred ways for this contact to communicate.
  contactMethodOptions: { value: string; label: string; icon: string }[] = [
    { value: 'Phone',     label: 'Phone',     icon: 'phone' },
    { value: 'WhatsApp',  label: 'WhatsApp',  icon: 'chat' },
    { value: 'SMS',       label: 'SMS',       icon: 'sms' },
    { value: 'Email',     label: 'Email',     icon: 'email' },
    { value: 'LinkedIn',  label: 'LinkedIn',  icon: 'work' },
    { value: 'Telegram',  label: 'Telegram',  icon: 'send' },
    { value: 'Signal',    label: 'Signal',    icon: 'shield' },
    { value: 'In person', label: 'In person', icon: 'handshake' },
    { value: 'Other',     label: 'Other',     icon: 'more_horiz' },
  ];
  selectedContactMethods = new Set<string>();

  // Introducer dropdown (referrer of this lead).
  introducers: IntroducerOption[] = [];
  introducersLoading = false;

  // Person-in-charge: organisation users to pick from.
  teamMembers: TeamMemberOption[] = [];
  teamMembersLoading = false;

  // Inline "Add new introducer" mini-form state.
  addingIntroducer = false;
  savingIntroducer = false;
  newIntroducer = { name: '', email: '', phone: '' };
  introducerDuplicates: { id: number; name: string; email: string; phone: string | null }[] = [];
  private introducerLookup$ = new Subject<void>();

  // Business-card scan state.
  scanning = false;
  scanWarnings: string[] = [];
  scanStatus: 'idle' | 'ok' | 'partial' | 'error' = 'idle';
  scanMessage = '';

  // Duplicate detection.
  duplicates: PhonebookContactVM[] = [];
  duplicateOverridden = false;

  // Notes thread (edit mode only — needs an existing contact id).
  notes: PhonebookContactNoteVM[] = [];
  notesLoading = false;
  newNoteBody = '';
  addingNote = false;

  private subs: Subscription[] = [];
  private dupCheck$ = new Subject<void>();

  // Unsaved contact kept on the server until saved, so a dialog closed by mistake loses nothing.
  // The business-card photo is not drafted, only what it filled in.
  readonly autosave = formAutosave<any>({
    formKey: 'corporate.phonebook-contact',
    recordKey: () => this.isEdit ? this.data.contact?.id : null,
    read: () => this.form
      ? { ...this.form.getRawValue(), preferredContactMethods: Array.from(this.selectedContactMethods) }
      : null,
    write: v => {
      const { preferredContactMethods, ...values } = v ?? {};
      this.form.patchValue(values);
      this.selectedContactMethods = new Set<string>(preferredContactMethods ?? []);
      // Same as loadIndustries: a typed-in industry must stay selectable.
      const industry = values.industry;
      if (industry && !this.industrySelectOptions.some((o) => o.value === industry)) {
        this.industrySelectOptions = [{ value: industry, label: industry }, ...this.industrySelectOptions];
      }
      this.form.markAsDirty();
    },
    title: () => [this.form?.value?.firstName, this.form?.value?.lastName].filter(Boolean).join(' ')
      || this.form?.value?.company
  });

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ContactFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContactFormDialogData,
    private phonebookService: PhonebookService,
    private formsService: CorporateFormsService,
    private userService: UserService,
    private companiesService: CompaniesService,
  ) {}

  ngOnInit(): void {
    this.isEdit = this.data.mode === 'edit';
    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: [''],
      email: ['', [Validators.email]],
      mobilePhone: [''],
      workPhone: [''],
      contactType: ['Work', Validators.required],
      company: [''],
      jobTitle: [''],
      department: [''],
      address: [''],
      city: [''],
      country: [''],
      tic: [''],
      vatNumber: [''],
      notes: [''],
      // ─── Lead ───
      leadRating: [5],
      industry: [''],
      ownerName: [''],
      ownerAzureObjectId: [''],
      introducerId: [null],
      linkedInUrl: [''],
      website: [''],
      facebook: [''],
      instagram: [''],
    });

    if (this.isEdit && this.data.contact) {
      const c = this.data.contact;
      this.form.patchValue(c);
      this.form.patchValue({ leadRating: c.leadRating ?? 5 });
      this.selectedContactMethods = new Set(c.preferredContactMethods || []);
    }

    this.loadIndustries();
    this.loadIntroducers();
    this.loadTeamMembers();

    if (!this.isEdit) {
      // Autosave starts once the owner is prefilled, so the prefill is not taken for an edit.
      this.prefillOwnerFromCurrentUser();
    } else if (this.data.contact) {
      this.loadNotes(this.data.contact.id);
      this.autosave.start();
    }

    // Keep ownerName in sync with the selected team member.
    this.subs.push(
      this.form.get('ownerAzureObjectId')!.valueChanges.subscribe((oid: string) => {
        const member = this.teamMembers.find((m) => m.azureObjectId === oid);
        if (member) {
          this.form.patchValue({ ownerName: member.name }, { emitEvent: false });
        }
      })
    );

    // Debounced duplicate lookup on email / phone changes.
    this.subs.push(
      merge(
        this.form.get('email')!.valueChanges,
        this.form.get('mobilePhone')!.valueChanges,
        this.form.get('workPhone')!.valueChanges,
      )
        .pipe(debounceTime(500))
        .subscribe(() => this.runDuplicateCheck())
    );

    // Debounced introducer-duplicate lookup while editing the mini-form.
    this.subs.push(
      this.introducerLookup$.pipe(debounceTime(400)).subscribe(() => this.runIntroducerLookup())
    );
  }

  private runIntroducerLookup(): void {
    const email = (this.newIntroducer.email || '').trim();
    const phone = (this.newIntroducer.phone || '').trim();
    if (!email && !phone) { this.introducerDuplicates = []; return; }
    this.formsService.lookupIntroducer(email || null, phone || null).subscribe({
      next: (matches) => { this.introducerDuplicates = matches || []; },
      error: () => { this.introducerDuplicates = []; },
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ─── Industries (fetched from the system) ───
  private loadIndustries(): void {
    this.industriesLoading = true;
    this.formsService.getComplianceIndustries().subscribe({
      next: (list: { id: number; title: string }[]) => {
        const seen = new Set<string>();
        this.industrySelectOptions = (list || [])
          .map((i) => (i?.title || '').trim())
          .filter((t) => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()))
          .sort((a, b) => a.localeCompare(b))
          .map((t) => ({ value: t, label: t }));
        // If the contact already has an industry not in the list, keep it
        // visible as a selectable option.
        const current = this.form?.value?.industry;
        if (current && !this.industrySelectOptions.some((o) => o.value === current)) {
          this.industrySelectOptions = [{ value: current, label: current }, ...this.industrySelectOptions];
        }
        this.industriesLoading = false;
      },
      error: () => { this.industriesLoading = false; },
    });
  }

  // ─── Inline "Add new introducer" flow ───
  startAddIntroducer(): void {
    this.addingIntroducer = true;
    this.introducerDuplicates = [];
    this.newIntroducer = { name: '', email: '', phone: '' };
  }

  cancelAddIntroducer(): void {
    this.addingIntroducer = false;
    this.introducerDuplicates = [];
  }

  canSaveNewIntroducer(): boolean {
    const n = this.newIntroducer;
    return !!(n.name && n.name.trim() && n.email && /.+@.+\..+/.test(n.email));
  }

  onNewIntroducerContactChange(): void {
    // Debounce the duplicate lookup so we don't spam the API while typing.
    this.introducerLookup$.next();
  }

  saveNewIntroducer(): void {
    if (!this.canSaveNewIntroducer()) return;
    this.savingIntroducer = true;
    const payload = {
      name: this.newIntroducer.name.trim(),
      email: this.newIntroducer.email.trim(),
      phone: this.newIntroducer.phone?.trim() || null,
    };
    this.formsService.createIntroducerQuick(payload).subscribe({
      next: (created) => {
        // Prepend to the dropdown options & select it.
        this.introducers = [{ id: created.id, name: created.name }, ...this.introducers];
        this.rebuildIntroducerOptions();
        this.form.patchValue({ introducerId: created.id });
        this.savingIntroducer = false;
        this.addingIntroducer = false;
      },
      error: () => { this.savingIntroducer = false; },
    });
  }

  useExistingIntroducer(d: { id: number; name: string }): void {
    // Make sure the matched record is selectable, then pick it.
    if (!this.introducers.some((i) => i.id === d.id)) {
      this.introducers = [{ id: d.id, name: d.name }, ...this.introducers];
      this.rebuildIntroducerOptions();
    }
    this.form.patchValue({ introducerId: d.id });
    this.cancelAddIntroducer();
  }

  // ─── Owner (person in charge) ───
  private prefillOwnerFromCurrentUser(): void {
    this.userService.getCurrentUser(false).pipe(take(1)).subscribe({
      next: (u) => {
        if (u) {
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
          this.form.patchValue({
            ownerName: this.form.value.ownerName || name || u.email || '',
            ownerAzureObjectId: u.azureObjectId || '',
          });
        }
        this.autosave.start();
      },
      error: () => { /* non-fatal: owner can be typed manually */ this.autosave.start(); },
    });
  }

  // ─── Team members (person in charge) ───
  private loadTeamMembers(): void {
    this.teamMembersLoading = true;
    this.companiesService.getOrganizationUsers().subscribe({
      next: (list: any[]) => {
        this.teamMembers = (list || [])
          .filter((u) => u && u.azureObjectId && (u.isActive !== false))
          .map((u) => ({
            azureObjectId: u.azureObjectId,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || u.azureObjectId,
            email: u.email || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // If the current owner isn't in the list (e.g. external user), keep
        // them visible as a synthetic option so the select shows the name.
        const oid = this.form.value.ownerAzureObjectId;
        if (oid && !this.teamMembers.some((m) => m.azureObjectId === oid)) {
          this.teamMembers.unshift({
            azureObjectId: oid,
            name: this.form.value.ownerName || '(current)',
            email: '',
          });
        }
        this.rebuildTeamMemberOptions();
        this.teamMembersLoading = false;
      },
      error: () => { this.teamMembersLoading = false; },
    });
  }

  private rebuildTeamMemberOptions(): void {
    this.teamMemberSelectOptions = this.teamMembers.map((m) => ({
      value: m.azureObjectId,
      label: m.name,
      sub: m.email || undefined,
    }));
  }

  // ─── Introducers ───
  private loadIntroducers(): void {
    this.introducersLoading = true;
    this.formsService.getIntroducers().subscribe({
      next: (list: IBusiness[]) => {
        this.introducers = (list || [])
          .filter((b) => b && b.id != null && (b.name || b.companyName))
          .map((b) => ({ id: b.id, name: (b.name || b.companyName) as string }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.rebuildIntroducerOptions();
        this.introducersLoading = false;
      },
      error: () => { this.introducersLoading = false; },
    });
  }

  private rebuildIntroducerOptions(): void {
    this.introducerSelectOptions = this.introducers.map((i) => ({ value: i.id, label: i.name }));
  }

  // ─── Business-card scan ───
  onCardSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.scanning = true;
    this.scanWarnings = [];
    this.scanStatus = 'idle';
    this.scanMessage = '';
    this.phonebookService.scanCard(file).subscribe({
      next: (res) => {
        const patch: Record<string, unknown> = {};
        const map: [keyof typeof res, string][] = [
          ['firstName', 'firstName'], ['lastName', 'lastName'], ['email', 'email'],
          ['mobilePhone', 'mobilePhone'], ['workPhone', 'workPhone'], ['company', 'company'],
          ['jobTitle', 'jobTitle'], ['department', 'department'], ['address', 'address'],
          ['city', 'city'], ['country', 'country'], ['website', 'website'],
          ['linkedInUrl', 'linkedInUrl'],
        ];
        let filled = 0;
        for (const [src, ctrl] of map) {
          const val = res[src];
          if (val != null && String(val).trim() !== '') {
            patch[ctrl] = val;
            filled++;
          }
        }
        this.form.patchValue(patch);
        this.scanWarnings = res.warnings || [];
        this.scanning = false;
        if (filled > 0) {
          this.scanStatus = filled >= 4 ? 'ok' : 'partial';
          this.scanMessage = `Filled ${filled} field${filled > 1 ? 's' : ''} from the card — please review.`;
          this.activeSection = 'personal';
          this.runDuplicateCheck();
        } else {
          this.scanStatus = 'error';
          this.scanMessage = 'No details could be read. Try a sharper, well-lit photo.';
        }
      },
      error: (err) => {
        this.scanning = false;
        this.scanStatus = 'error';
        this.scanMessage =
          err?.error?.message ||
          err?.error?.error ||
          'Could not read the business card. Please fill the form manually.';
      },
    });
    // Allow re-selecting the same file.
    input.value = '';
  }

  // ─── Duplicate detection ───
  private runDuplicateCheck(): void {
    const email = (this.form.value.email || '').trim();
    const phone = (this.form.value.mobilePhone || this.form.value.workPhone || '').trim();
    if (!email && !phone) {
      this.duplicates = [];
      return;
    }
    this.phonebookService.lookup(email || null, phone || null).subscribe({
      next: (matches) => {
        const editingId = this.data.contact?.id;
        this.duplicates = (matches || []).filter((m) => m.id !== editingId);
      },
      error: () => { this.duplicates = []; },
    });
  }

  dismissDuplicates(): void {
    this.duplicateOverridden = true;
  }

  // ─── Preferred contact methods ───
  toggleContactMethod(value: string): void {
    if (this.selectedContactMethods.has(value)) this.selectedContactMethods.delete(value);
    else this.selectedContactMethods.add(value);
  }
  isMethodSelected(value: string): boolean {
    return this.selectedContactMethods.has(value);
  }

  /**
   * Close this dialog and ask the caller to open the matched contact in the
   * edit dialog instead. Used by the duplicate-warning banner.
   */
  openMatch(match: PhonebookContactVM): void {
    this.dialogRef.close({ openExisting: match });
  }

  // ─── Notes thread (edit mode) ───
  private loadNotes(contactId: number): void {
    this.notesLoading = true;
    this.phonebookService.listNotes(contactId)
      .pipe(finalize(() => (this.notesLoading = false)))
      .subscribe({
        next: (n) => (this.notes = n),
        error: () => (this.notes = []),
      });
  }

  addNote(): void {
    const body = (this.newNoteBody || '').trim();
    const contactId = this.data.contact?.id;
    if (!body || !contactId) return;

    this.addingNote = true;
    this.phonebookService.addNote(contactId, { body })
      .pipe(finalize(() => (this.addingNote = false)))
      .subscribe({
        next: (note) => {
          this.notes = [note, ...this.notes];
          this.newNoteBody = '';
        },
        error: () => { /* keep composer text so user can retry */ },
      });
  }

  deleteNote(note: PhonebookContactNoteVM): void {
    const contactId = this.data.contact?.id;
    if (!contactId) return;
    this.phonebookService.deleteNote(contactId, note.id).subscribe({
      next: () => (this.notes = this.notes.filter((n) => n.id !== note.id)),
    });
  }

  formatNoteTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return 'yesterday';
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ─── Section navigation ───
  setSection(s: FormSection): void {
    this.activeSection = s;
  }

  hasSectionError(section: FormSection): boolean {
    const map: Record<FormSection, string[]> = {
      personal: ['firstName', 'email', 'mobilePhone', 'workPhone', 'lastName'],
      work: ['contactType', 'company', 'jobTitle', 'department', 'tic', 'vatNumber'],
      lead: ['leadRating', 'industry', 'ownerName', 'introducerId', 'linkedInUrl', 'website'],
      location: ['address', 'city', 'country'],
      notes: ['notes'],
    };
    return map[section].some((f) => this.form.get(f)?.invalid && this.form.get(f)?.touched);
  }

  hasError(field: string, error: string): boolean {
    const c = this.form.get(field);
    return !!c?.hasError(error) && (c.touched || c.dirty);
  }

  isFieldInvalid(field: string): boolean {
    const c = this.form.get(field);
    return !!c?.invalid && (c.touched || c.dirty);
  }

  // ─── Save ───
  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      for (const s of this.sections) {
        if (this.hasSectionError(s.key)) {
          this.activeSection = s.key;
          break;
        }
      }
      return;
    }

    const raw = this.form.value;
    const industry = (raw.industry || '').trim() || null;
    const introducer = this.introducers.find((i) => i.id === raw.introducerId);

    const leadFields = {
      leadRating: raw.leadRating != null ? Number(raw.leadRating) : null,
      industry,
      ownerName: raw.ownerName || null,
      ownerAzureObjectId: raw.ownerAzureObjectId || null,
      introducerId: raw.introducerId ?? null,
      introducerName: introducer ? introducer.name : null,
      linkedInUrl: raw.linkedInUrl || null,
      website: raw.website || null,
      facebook: raw.facebook || null,
      instagram: raw.instagram || null,
    };

    const base = {
      firstName: raw.firstName,
      lastName: raw.lastName || null,
      email: raw.email || null,
      mobilePhone: raw.mobilePhone || null,
      workPhone: raw.workPhone || null,
      company: raw.company || null,
      contactType: raw.contactType,
      jobTitle: raw.jobTitle || null,
      department: raw.department || null,
      address: raw.address || null,
      city: raw.city || null,
      country: raw.country || null,
      tic: raw.tic || null,
      vatNumber: raw.vatNumber || null,
      notes: raw.notes || null,
      preferredContactMethods: this.selectedContactMethods.size
        ? Array.from(this.selectedContactMethods)
        : null,
      ...leadFields,
    };

    // The dialog hands the contact to its opener, which does the API call; closing with it is
    // this form's save, so the draft goes here.
    this.autosave.complete();
    if (this.isEdit) {
      this.dialogRef.close(base as UpdatePhonebookContactDTO);
    } else {
      this.dialogRef.close(base as CreatePhonebookContactDTO);
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
